import { HttpClient, HttpEventType, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { endpoints } from '../../core/constants/endpoints';
import {
  IUserAttachment,
  IUserAttachmentRaw,
  toUserAttachment
} from '../interfaces/user-attachment.interface';

export interface IChatAttachmentUploadRequest {
  file: File;
  idempotencyKey: string;
}

/** O upload emite progresso até concluir; `attachment` só vem no último evento. */
export interface IChatAttachmentUploadEvent {
  progress: number;
  attachment: IUserAttachment | null;
}

@Injectable({ providedIn: 'root' })
export class ChatAttachmentsApi {
  private readonly baseUrl = `${environment.ceppemAiApi}/${endpoints.ceppemAi.chatAttachments}`;

  constructor(private http: HttpClient) {}

  upload(
    request: IChatAttachmentUploadRequest
  ): Observable<IChatAttachmentUploadEvent> {
    const form = new FormData();
    form.append('file', request.file, request.file.name);

    const headers = new HttpHeaders({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Idempotency-Key': request.idempotencyKey
    });

    return this.http
      .post<IUserAttachmentRaw>(this.baseUrl, form, {
        headers,
        observe: 'events',
        reportProgress: true
      })
      .pipe(
        filter(
          (event) =>
            event.type === HttpEventType.UploadProgress ||
            event.type === HttpEventType.Response
        ),
        map((event) => {
          if (event.type === HttpEventType.UploadProgress) {
            return {
              progress: event.total
                ? Math.round((event.loaded / event.total) * 100)
                : 0,
              attachment: null
            };
          }
          const body = (event as { body: IUserAttachmentRaw | null }).body;
          return {
            progress: 100,
            attachment: body ? toUserAttachment(body) : null
          };
        })
      );
  }

  getById(attachmentId: string): Observable<IUserAttachment> {
    return this.http
      .get<IUserAttachmentRaw>(`${this.baseUrl}/${encodeURIComponent(attachmentId)}`)
      .pipe(map(toUserAttachment));
  }

  delete(attachmentId: string): Observable<IUserAttachment> {
    return this.http
      .delete<IUserAttachmentRaw>(`${this.baseUrl}/${encodeURIComponent(attachmentId)}`)
      .pipe(map(toUserAttachment));
  }

  downloadSource(attachmentId: string): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/${encodeURIComponent(attachmentId)}/source`,
      { responseType: 'blob' }
    );
  }
}
